import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Avatar,
  IconButton,
  Divider,
  Drawer,
  TextField,
  Checkbox,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { colorPalette, getInitials } from "../../utils";

interface AssigneeDrawerProps {
  open: boolean;
  onClose: () => void;
  users: any[];
  currentAssignees: any[];
  onSave: (selectedUserIds: string[]) => Promise<void>;
  isLoading?: boolean;
}

const AssigneeDrawer: React.FC<AssigneeDrawerProps> = ({
  open,
  onClose,
  users,
  currentAssignees,
  onSave,
  isLoading = false,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize selected users when drawer opens
  useEffect(() => {
    if (open) {
      setSelectedUsers(currentAssignees?.map((u: any) => u._id) || []);
      setSearchQuery("");
    }
  }, [open, currentAssignees]);

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    return users.filter((user: any) =>
      user.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [users, searchQuery]);

  // Check if user is selected
  // const isUserSelected = (userId: string) => selectedUsers.includes(userId);

  // Toggle user selection
  const handleToggleUser = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle save
  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave(selectedUsers);
      onClose();
    } catch (error) {
      console.error("Failed to save assignees:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Get selected users data for display
  const selectedUsersData = useMemo(() => {
    return users.filter((user: any) => selectedUsers.includes(user._id));
  }, [users, selectedUsers]);

  // Get unselected users data for display
  const unselectedUsersData = useMemo(() => {
    return filteredUsers.filter((user: any) => !selectedUsers.includes(user._id));
  }, [filteredUsers, selectedUsers]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 350 },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t("assignUsers")}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search */}
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            size="small"
            placeholder={t("Search users...")}
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* User List */}
        <Box sx={{ flex: 1, overflow: "auto", px: 2 }}>
          {/* Selected Users */}
          {selectedUsersData.length > 0 && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 1, display: "block" }}
              >
                {t("selected")} ({selectedUsersData.length})
              </Typography>
              {selectedUsersData
                .filter((user: any) =>
                  user.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((user: any, index: number) => (
                  <Box
                    key={user._id}
                    onClick={() => handleToggleUser(user._id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      mb: 0.5,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 1.5,
                        backgroundColor: colorPalette[index % colorPalette.length],
                        fontSize: 13,
                      }}
                    >
                      {getInitials(user.fullName || "")}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {user.fullName}
                      </Typography>
                      {user.email && (
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      )}
                    </Box>
                    <Checkbox checked={true} size="small" />
                  </Box>
                ))}
              <Divider sx={{ my: 1.5 }} />
            </>
          )}

          {/* Available Users */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, mb: 1, display: "block" }}
          >
            {t("availableUsers")}
          </Typography>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : unselectedUsersData.length > 0 ? (
            unselectedUsersData.map((user: any, index: number) => (
              <Box
                key={user._id}
                onClick={() => handleToggleUser(user._id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  mb: 0.5,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    mr: 1.5,
                    backgroundColor: colorPalette[index % colorPalette.length],
                    fontSize: 13,
                  }}
                >
                  {getInitials(user.fullName || "")}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {user.fullName}
                  </Typography>
                  {user.email && (
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  )}
                </Box>
                <Checkbox checked={false} size="small" />
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 2, textAlign: "center", fontStyle: "italic" }}
            >
              {t("noUsersAvailable")}
            </Typography>
          )}
        </Box>

        {/* Footer with Save Button */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            gap: 1,
          }}
        >
          <Button variant="outlined" fullWidth onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? t("Saving...") : t("Save")}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AssigneeDrawer;
